import { useState, useEffect, useCallback, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import type { AuthResponse } from '../api/authApi';

/**
 * JWT payload structure with custom claims from Cresta backend.
 */
interface JwtPayload {
    sub: string;        // email
    salon_id?: string;
    role?: string;      // OWNER, MANAGER, STYLIST, RECEPTIONIST
    plan?: string;      // DEMO, FREE, BASIC, PRO
    employee_id?: string; // NEW linked employee ID
    iat?: number;
    exp?: number;
}



/**
 * Plan limits configuration.
 * These should match the backend's subscription_plans table.
 */
const PLAN_LIMITS: Record<string, PlanLimits> = {
    DEMO: {
        employees: 2,
        clients: 50,
        products: 20,
        storageLocations: 1,
        appointmentsMonthly: 50,
        whatsappMonthly: 0,
        features: {
            photoUpload: false,
            visualHistory: false,
            customBranding: false,
            advancedReports: false,
            permissionPanel: false,
            apiAccess: false,
        }
    },
    PRO: {
        employees: Infinity,
        clients: Infinity,
        products: Infinity,
        storageLocations: Infinity,
        appointmentsMonthly: Infinity,
        whatsappMonthly: Infinity,
        features: {
            photoUpload: true,
            visualHistory: true,
            customBranding: true,
            advancedReports: true,
            permissionPanel: true,
            apiAccess: true,
        }
    },
    BASIC: {
        employees: 10,
        clients: 500,
        products: 500,
        storageLocations: 2,
        appointmentsMonthly: Infinity,
        whatsappMonthly: 500,
        features: {
            photoUpload: true,
            visualHistory: false,
            customBranding: true,
            advancedReports: false,
            permissionPanel: true,
            apiAccess: false,
        }
    },
    FREE: {
        employees: 1,
        clients: 50,
        products: 20,
        storageLocations: 1,
        appointmentsMonthly: 50,
        whatsappMonthly: 0,
        features: {
            photoUpload: false,
            visualHistory: false,
            customBranding: false,
            advancedReports: false,
            permissionPanel: false,
            apiAccess: false,
        }
    },
    // Fallback for unknown plans
    NONE: {
        employees: 0,
        clients: 0,
        products: 0,
        storageLocations: 0,
        appointmentsMonthly: 0,
        whatsappMonthly: 0,
        features: {
            photoUpload: false,
            visualHistory: false,
            customBranding: false,
            advancedReports: false,
            permissionPanel: false,
            apiAccess: false,
        }
    }
};

export interface PlanLimits {
    employees: number;
    clients: number;
    products: number;
    storageLocations: number;
    appointmentsMonthly: number;
    whatsappMonthly: number;
    features: {
        photoUpload: boolean;
        visualHistory: boolean;
        customBranding: boolean;
        advancedReports: boolean;
        permissionPanel: boolean;
        apiAccess: boolean;
    };
}

export type FeatureKey = keyof PlanLimits['features'];
export type LimitKey = Exclude<keyof PlanLimits, 'features'>;

export interface SubscriptionInfo {
    plan: string;
    role: string;
    employeeId?: string; // NEW logic
    limits: PlanLimits;
    isLoading: boolean;
    isPaid: boolean;
    isDemo: boolean;
    hasFeature: (feature: FeatureKey) => boolean;
    getLimit: (key: LimitKey) => number;
    isOwner: boolean;
    isManager: boolean;
    isSuperAdmin: boolean;
    isBetaMode: boolean;
    isFreeMode: boolean;
    subscriptionExpiresAt: string | null;
    daysRemaining: number | null;
}

/**
 * Hook to access subscription and role information from the JWT.
 * Decodes the token and provides convenient accessors for plan limits and features.
 */
// Feature Flag used to bypass all subscription limits
const FREE_MODE = import.meta.env.VITE_FREE_MODE === 'true';
const BETA_MODE = import.meta.env.VITE_BETA_MODE === 'true';

export const useSubscription = (): SubscriptionInfo => {
    const [plan, setPlan] = useState<string>('NONE');
    const [role, setRole] = useState<string>('NONE');
    const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
    const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadFromToken = () => {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                setIsLoading(false);
                return;
            }

            try {
                const user: AuthResponse = JSON.parse(userStr);
                setSubscriptionExpiresAt(user.subscriptionExpiresAt || null);
                
                if (user.token) {
                    const decoded = jwtDecode<JwtPayload>(user.token);
                    setPlan(decoded.plan || 'DEMO');
                    setRole(decoded.role || user.role || 'OWNER');
                    setEmployeeId(decoded.employee_id);
                } else {
                    // Fallback to AuthResponse role (legacy)
                    setRole(user.role || 'OWNER');
                    setPlan('DEMO');
                }
            } catch (error) {
                console.error('Error decoding JWT:', error);
                setPlan('DEMO');
                setRole('OWNER');
            }
            setIsLoading(false);
        };

        loadFromToken();

        // Listen for storage changes
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'user') {
                loadFromToken();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const limits = useMemo(() => PLAN_LIMITS[plan] || PLAN_LIMITS.NONE, [plan]);

    const isPaid = useMemo(() => !['DEMO', 'FREE', 'NONE'].includes(plan), [plan]);
    const isDemo = useMemo(() => plan === 'DEMO', [plan]);
    const isOwner = useMemo(() => role === 'OWNER', [role]);
    const isManager = useMemo(() => role === 'MANAGER', [role]);
    const isSuperAdmin = useMemo(() => role === 'SUPER_ADMIN', [role]);

    const daysRemaining = useMemo(() => {
        if (!subscriptionExpiresAt) return null;
        try {
            const expiry = new Date(subscriptionExpiresAt);
            const now = new Date();
            const diffTime = expiry.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return Math.max(0, diffDays);
        } catch (e) {
            console.error('Error calculating days remaining:', e);
            return null;
        }
    }, [subscriptionExpiresAt]);

    // Override limits and features for SUPER_ADMIN or FREE_MODE
    const getLimit = useCallback((key: LimitKey): number => {
        if (isSuperAdmin || FREE_MODE || BETA_MODE) return Infinity;
        return limits[key] ?? 0;
    }, [limits, isSuperAdmin]);

    const hasFeature = useCallback((feature: FeatureKey): boolean => {
        if (isSuperAdmin || FREE_MODE || BETA_MODE) return true;
        return limits.features[feature] ?? false;
    }, [limits, isSuperAdmin]);

    return {
        plan,
        role,
        employeeId,
        limits,
        isLoading,
        isPaid,
        isDemo,
        hasFeature,
        getLimit,
        isOwner,
        isManager,
        isSuperAdmin,
        isBetaMode: BETA_MODE,
        isFreeMode: FREE_MODE,
        subscriptionExpiresAt,
        daysRemaining
    };
};

/**
 * Get subscription info synchronously (for use outside React components).
 */
export const getSubscriptionSync = (): { plan: string; role: string; employeeId?: string } => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return { plan: 'NONE', role: 'NONE' };

    try {
        const user: AuthResponse = JSON.parse(userStr);
        if (user.token) {
            const decoded = jwtDecode<JwtPayload>(user.token);
            return {
                plan: decoded.plan || 'DEMO',
                role: decoded.role || user.role || 'OWNER',
                employeeId: decoded.employee_id
            };
        }
        return { plan: 'DEMO', role: user.role || 'OWNER' };
    } catch {
        return { plan: 'DEMO', role: 'OWNER' };
    }
};
